import {AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {FormBuilder, NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {ReplaySubject} from 'rxjs';
import {UserPermissions} from '../../../../shared/interface/user-permissions';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from '../../../../shared/services/auth.service';
import {FilterService} from '../../../../shared/services/filter.service';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../../shared/ngrx/reducers';
import {delay, distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../../shared/interface/response/counted-and-paginated';
import {getCurrentRouteState} from '../../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../../shared/ngrx/route-serializer';
import {ConfirmDialogComponent} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {RiskConfiguration} from '../../../../shared/interface/risk-configuration';
import {RiskService} from '../../../../shared/services/risk.service';
import {InsuranceCompany} from '../../../../shared/interface/insurance-company';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {HttpErrorResponse} from '@angular/common/http';
import {Uuid} from '../../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../../shared/components/job-result-dialog/job-result-dialog.component';
import {EditRiskModalComponent} from '../edit-risk-modal/edit-risk-modal.component';
import {CommissionTableListItem} from "../../../../shared/interface/commission-table";
import {CommissionTableService} from "../../../../shared/services/commission-table.service";
import {TableViewStorageService} from "../../../../shared/services/table-view-storage.service";

@Component({
  selector: 'app-all-risks-list',
  templateUrl: './all-risks-list.component.html',
  styleUrls: ['./all-risks-list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class AllRisksListComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild('paginator') paginator: MatPaginator;
  @Output() initialFiltersRisk = new EventEmitter<string>();

  commissionTables: CommissionTableListItem[] = [];
  private readonly commissionTableStorageName = 'risks-commission-tables';
  selectedCommissionTableUuids: string[] = [];

  initialFilters: any;
  items: RiskConfiguration[] = [];
  displayedColumns: string[] = ['logo', 'insuranceCompany', 'importer', 'internalRisk', 'insuranceCompanyType', 'actions'];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  selectedInsuranceCompanies: InsuranceCompany[] = [];
  expandedElement: RiskConfiguration = null;
  riskConfigurationLog: ReplaySubject<RiskConfiguration> = new ReplaySubject<RiskConfiguration>(1);
  saveExpandedElement: RiskConfiguration = null;
  modify = false;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private riskService: RiskService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
    private fb: FormBuilder,
    private commissionTableService: CommissionTableService,
    private tableViewStorageService: TableViewStorageService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.riskService.riskConfigurationListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<RiskConfiguration>) => {
          this.items = e.items;
          this.totalData = e.pagination.total;

          if (this.initialFilters?.uuid) {
            this.expandElement(this.items[0]);
          }

          if (this.saveExpandedElement) {
            this.items.forEach((riskConfiguration: RiskConfiguration) => {
              if (riskConfiguration.uuid === this.saveExpandedElement.uuid) {
                this.expandedElement = riskConfiguration;
                this.riskConfigurationLog.next(riskConfiguration);
              }
            });
            this.saveExpandedElement = null;
          }
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      uuid: 'string',
      in$insuranceCompany: 'int[]',
      in$importer: 'int[]',
      in$internalRisk: 'int[]',
      group: 'string',
      risk: 'string',
    }, filters);
    this.initialFiltersRisk.emit(this.initialFilters.risk);
  }

  ngOnInit(): void {
    this.commissionTableService
      .getOptions()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((tables: CommissionTableListItem[]) => {
        this.commissionTables = tables
          .sort((a, b) =>
            (a.validFrom || '').localeCompare(b.validFrom || '')
          );
        this.initializeCommissionTableSelection();
      });
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        ...this.filtersForm.value
      })]);
    });

    this.subscribeQueryParams();
  }

  subscribeQueryParams(): void {
    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((routerState: RouterStateUrl) => {
        this.parseFilters(routerState.params);
        this.paginator.pageIndex = routerState.params.page;
        this.paginator.pageSize = routerState.params.pageSize;
        this.loadListData();
      })
    ;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.riskService.loadList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
  }

  buildFilters(): any {
    return this.initialFilters;
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  downloadReport(): void {
    this.riskService.downloadReport(this.initialFilters)
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  delete(rc: RiskConfiguration): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć konfigurację?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.riskService.deleteConfiguration(rc.uuid)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto konfigurację', '', {duration: 2000});
              this.loadListData();
            },
            error: (error: HttpErrorResponse) => {
              this.snack.open('Przy usuwaniu wystąpił błąd', error.status.toString(), {duration: 2000});
            }
          });
      }
    });
  }

  edit(rc: RiskConfiguration): void {
    const dialogRef = this.dialog.open(EditRiskModalComponent, {
      width: '90vw',
      data: {riskConfiguration: rc},
      maxHeight: '90vh',
    });

    dialogRef.afterClosed().subscribe(() => {
      this.loadListData();
    });
  }

  expandElement(
    riskConfiguration: RiskConfiguration
  ): void {

    const expand = this.expandedElement !== riskConfiguration;

    this.expandedElement = expand ? riskConfiguration : null;

    this.modify = false;

    if (expand) {
      this.riskConfigurationLog.next(riskConfiguration);
    }
  }

  modificationClosed(): void {
    this.saveExpandedElement = this.expandedElement;
    this.loadListData();
  }

  private initializeCommissionTableSelection(): void {
    const availableUuids = this.commissionTables.map((table: CommissionTableListItem) => table.uuid);

    const table2023 = this.commissionTables.find((table: CommissionTableListItem) => table.shortName === '2023');
    const latestTable =
      this.commissionTables.length > 0
        ? this.commissionTables[this.commissionTables.length - 1]
        : null;

    const defaultUuids = [table2023?.uuid, latestTable?.uuid]
      .filter((uuid: string | undefined) => !!uuid)
      .filter(
        (
          uuid: string,
          index: number,
          array: string[]
        ) => array.indexOf(uuid) === index
      );

    let selectedUuids = this.tableViewStorageService.getColumns(this.commissionTableStorageName, availableUuids, defaultUuids);
    if (table2023 && selectedUuids.indexOf(table2023.uuid) === -1) {
      selectedUuids = [table2023.uuid, ...selectedUuids];
      this.tableViewStorageService.setColumns(this.commissionTableStorageName, selectedUuids);
    }

    this.selectedCommissionTableUuids =
      selectedUuids;
  }

  commissionTableSelectionChanged(
    selectedUuids: string[]
  ): void {

    const availableUuids = this.commissionTables.map((table: CommissionTableListItem) => table.uuid);
    let selected = selectedUuids.filter((uuid: string) => availableUuids.indexOf(uuid) !== -1);
    const table2023 = this.commissionTables.find((table: CommissionTableListItem) => table.shortName === '2023');

    /*
     * 2023 zawsze musi pozostać.
     */
    if (table2023 && selected.indexOf(table2023.uuid) === -1) {
      selected = [table2023.uuid, ...selected];
    }

    this.selectedCommissionTableUuids =
      selected;

    this.tableViewStorageService.setColumns(
      this.commissionTableStorageName,
      selected
    );
  }

  get commissionTableSelectionLabel(): string {
    return this.visibleCommissionTables
      .map(
        (table: CommissionTableListItem) =>
          table.shortName
      )
      .join(', ');
  }

  get visibleCommissionTables():
    CommissionTableListItem[] {

    return this.commissionTables.filter(
      (table: CommissionTableListItem) =>
        this.selectedCommissionTableUuids.indexOf(
          table.uuid
        ) !== -1
    );
  }
}
